import {
    ChangeFundsRequest,
    CustomAllocation,
    FundCode,
    QuestionnaireConfig,
    QuestionnaireConfigFund,
    QuestionnaireConfigRecommendedStrategy,
    QuestionnaireConfigRecommendedStrategyFund,
    SfdrPreference,
    ZZJInformationRequest,
} from "@repo/api";
import { FundsRow, RootState, StepperState } from "@repo/logic";

export interface BuildChangeFundsRequestOptions {
    transactionId: string;
    dpsPrecontractualInfoName: string;
    phoneNumber: string;
    smsCode: string;
    recommendedStrategies: QuestionnaireConfigRecommendedStrategyFund[];
    funds: QuestionnaireConfigFund[];
}
// Helper function to select the recommended strategy based on what user has chosen
const selectedStrategy = (
    recommendedStrategyId: number | null | undefined,
    strategies: QuestionnaireConfigRecommendedStrategy[],
): QuestionnaireConfigRecommendedStrategy | null =>
    recommendedStrategyId == null ? null : (strategies.find((s) => s.id === recommendedStrategyId) ?? null);

// Map the recommended strategy allocations to CustomAllocation array
const mapRecommendedStrategyAllocations = (
    strategies: QuestionnaireConfigRecommendedStrategy[],
    funds: QuestionnaireConfigFund[],
    recommendedStrategyId?: number | null,
): CustomAllocation[] | null => {
    // Select the recommended strategy based on the provided recommendedStrategyId
    const recommendedStrategy = selectedStrategy(recommendedStrategyId, strategies);
    //Map fund ids to fund codes
    const fundsCodes = funds.map((fund) => ({
        id: fund.id,
        code: fund.code,
    }));
    // Return recommended strategy funds with codes
    return (
        recommendedStrategy?.funds?.map((fund) => ({
            fundCode: fundsCodes.find((f) => f.id === fund.id)?.code as FundCode,
            allocation: fund.recommendedPercentageAllocation,
        })) ?? null
    );
};

const mapCustomAllocatedFundsIdToCodes = (
    fundAllocation?: FundsRow[],
    config?: QuestionnaireConfig,
): CustomAllocation[] => {
    if (!fundAllocation || !config) return [];

    return fundAllocation
        .map((fund) => {
            const fundCode = config.funds.find((f) => f.id === fund.id)?.code;
            if (!fundCode) return null; //skip if fund code is not found
            return {
                fundCode,
                allocation: fund.value,
            };
        })
        .filter((f): f is CustomAllocation => f !== null);
};

const mapSfdrPreference = (value?: string | null): SfdrPreference | null => {
    switch (value) {
        case "yes_not_willing_non_sustainable":
            return SfdrPreference.Yes;
        case "partialYes_willing_non_sustainable":
            return SfdrPreference.PartialYes;
        case "no_not_interested":
            return SfdrPreference.No;
        default:
            return null;
    }
};

const isRecommendedStrategyAccepted = (
    fundAllocation?: FundsRow[],
    selectedStrategyArray?: QuestionnaireConfigRecommendedStrategy | null,
): boolean | null => {
    const recommendedFunds = selectedStrategyArray?.funds;
    if (!fundAllocation) return false; // When no fund allocations for the NewFund or ExistingFund?
    if (!recommendedFunds) return null; // When no recommended strategy selected

    for (const rec of recommendedFunds ?? []) {
        //At least one fund is not matching return false
        const alloc = fundAllocation.find((f) => f.id === rec.id);
        if (!alloc || alloc.value !== rec.recommendedPercentageAllocation) {
            return false;
        }
    }
    return true;
};

const normalizeChoices = (value: StepperState["questionnaireAnswers"]): string[] => {
    if (typeof value === "string") return [value];
    if (Array.isArray(value)) {
        if (value.every((v) => typeof v === "string")) return value;
        if (value.every((v) => typeof v === "boolean")) {
            return value.reduce<string[]>((acc, flag, idx) => {
                if (flag) acc.push(String(idx)); // keep original index of TRUE
                return acc;
            }, []);
        }
    }
    return [];
};

export const mapZZJInformationRequest = (
    stepperState: RootState["stepper"],
    config: QuestionnaireConfig,
): ZZJInformationRequest | null => {
    const selectedStrategyArray = selectedStrategy(
        stepperState.editInvestmentStrategyResult?.recommendedStrategyId,
        config.recommendedStrategies,
    );
    const acceptRecommendedStrategyNew = isRecommendedStrategyAccepted(stepperState.newFundRows, selectedStrategyArray);
    const acceptRecommendedStrategyExisting = isRecommendedStrategyAccepted(
        stepperState.existingFundRows,
        selectedStrategyArray,
    );
    const sfdrPreference = mapSfdrPreference(stepperState.sustainabilityId);

    if (!sfdrPreference || acceptRecommendedStrategyExisting === null || acceptRecommendedStrategyNew === null) {
        return null;
    }

    return {
        investmentQuestionnaire: {
            rejected: stepperState.questionnaireRejected,
            answers:
                stepperState.questionnaireAnswers && !stepperState.questionnaireRejected
                    ? Object.entries(stepperState.questionnaireAnswers).map(([questionId, choices]) => ({
                          questionId,
                          choices: normalizeChoices(choices),
                      }))
                    : [],
        },
        existingResources: !stepperState.existingFundRows
            ? undefined
            : {
                  acceptRecommendedStrategy: acceptRecommendedStrategyExisting,
                  customAllocation:
                      acceptRecommendedStrategyExisting === true
                          ? null
                          : mapCustomAllocatedFundsIdToCodes(stepperState.existingFundRows, config),
              },

        newResources: !stepperState.newFundRows
            ? undefined
            : {
                  acceptRecommendedStrategy: acceptRecommendedStrategyNew,
                  customAllocation:
                      acceptRecommendedStrategyNew === true
                          ? null
                          : mapCustomAllocatedFundsIdToCodes(stepperState.newFundRows, config),
              },
        sfdrPreference,
    };
};

// Returns a ChangeFundsRequest object built from the Redux state and provided data
export function BuildChangeFundsRequest(
    stepperState: RootState["stepper"],
    config: QuestionnaireConfig,
    dpsPrecontractualInfoName: string,
    smsCode: string,
): ChangeFundsRequest | null {
    const transactionId = stepperState.smsStepState?.transactionId ?? "";
    const authenticationPhone = stepperState.smsStepState?.phoneNumber ?? "";
    const questionnaireVersion = stepperState.questionnaireVersion ?? "";
    const recommendedStrategyAllocations = mapRecommendedStrategyAllocations(
        config.recommendedStrategies,
        config.funds,
        stepperState.editInvestmentStrategyResult?.recommendedStrategyId,
    );
    const zzjInformation = mapZZJInformationRequest(stepperState, config);

    // Check data if not valid return null to prevent call from the previous steps uncomment after testing
    if (!transactionId || !authenticationPhone || !smsCode || !zzjInformation || !recommendedStrategyAllocations) {
        return null;
    }

    return {
        transactionId,
        authenticationPhone,
        smsCode,
        dpsPrecontractualInfoName,
        zzjInformation,
        recommendedStrategy: recommendedStrategyAllocations,
        questionnaireVersion,
    };
}
